"use strict";
/**
 * The hockey pad's outbox (docs/nhl-model/PLAN.md §4.5). Unlike the football
 * tap queue these events change the price, so they go out ONE AT A TIME in
 * order: a goal must land before the UNDO that reverses it. Each carries a
 * client UUID the server is idempotent on, and the unsent ones are mirrored to
 * storage so a reload on stadium cellular loses nothing. A server refusal
 * (400: the event is invalid for the state) drops the event and reports it; a
 * network failure or 5xx retries with backoff.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.HockeyEventQueue = exports.isRefusal = exports.browserEventStorage = void 0;
exports.browserEventStorage = {
    load(key) {
        try {
            const raw = window.localStorage.getItem(key);
            const parsed = raw ? JSON.parse(raw) : [];
            return Array.isArray(parsed) ? parsed : [];
        }
        catch {
            return [];
        }
    },
    save(key, events) {
        try {
            if (events.length)
                window.localStorage.setItem(key, JSON.stringify(events));
            else
                window.localStorage.removeItem(key);
        }
        catch {
            // memory only
        }
    },
};
const isRefusal = (e) => !!e && typeof e === "object" && e.refused === true;
exports.isRefusal = isRefusal;
const BACKOFF_START_MS = 1000;
const BACKOFF_MAX_MS = 15000;
class HockeyEventQueue {
    constructor(key, send, storage, onReply, now = Date.now) {
        this.key = key;
        this.send = send;
        this.storage = storage;
        this.onReply = onReply;
        this.now = now;
        this.inflight = false;
        this.failures = 0;
        this.nextAttempt = 0;
        this.lastError = null;
        /** Events the server refused, newest last; kept for the status line. */
        this.refused = [];
        this.pending = storage.load(key);
    }
    get size() {
        return this.pending.length;
    }
    get unsent() {
        return this.pending;
    }
    add(event) {
        this.pending.push(event);
        this.storage.save(this.key, this.pending);
        this.nextAttempt = 0;
    }
    /** Send what is due, one event after another, until the queue is empty or a transport failure. */
    async flush() {
        if (this.inflight)
            return;
        while (this.pending.length > 0 && this.now() >= this.nextAttempt) {
            if (!(await this.sendOne()))
                return;
        }
    }
    async sendOne() {
        this.inflight = true;
        const event = this.pending[0];
        try {
            const reply = await this.send(event);
            this.settle(event);
            this.onReply(reply, event);
            return true;
        }
        catch (error) {
            if ((0, exports.isRefusal)(error)) {
                this.settle(event);
                this.refused.push({ event, message: error.message });
                this.lastError = null;
                return true; // the next event may still be valid
            }
            this.failures += 1;
            this.nextAttempt = this.now() + Math.min(BACKOFF_START_MS * 2 ** (this.failures - 1), BACKOFF_MAX_MS);
            this.lastError = error.message;
            return false;
        }
        finally {
            this.inflight = false;
        }
    }
    settle(event) {
        this.pending = this.pending.filter((e) => e.clientEventId !== event.clientEventId);
        this.storage.save(this.key, this.pending);
        this.failures = 0;
        this.nextAttempt = 0;
        this.lastError = null;
    }
}
exports.HockeyEventQueue = HockeyEventQueue;
